import { Link } from 'react-router-dom';

interface ArticleCardProps {
  id: number;
  title: string;
  excerpt: string;
  categoryName: string;
  publishedAt: string;
  cardImageUrl: string;
  views: number;
  comments?: number;
  isHot?: boolean;
  isExplosive?: boolean;
  variant?: 'default' | 'compact' | 'featured';
}

export default function ArticleCard({
  id,
  title,
  excerpt,
  categoryName,
  publishedAt,
  cardImageUrl,
  views,
  comments = 0,
  isHot = false,
  isExplosive = false,
  variant = 'default',
}: ArticleCardProps) {
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return String(v);
  };

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${id}`}
        className="group block bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300 transition-all overflow-hidden cursor-pointer"
      >
        <div className="aspect-[16/10] overflow-hidden relative">
          <img
            src={cardImageUrl}
            alt={title}
            title={`${title} - 吃瓜爆料网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
          {isExplosive && (
            <span className="absolute top-2 right-2 px-2 py-0.5 text-xs font-bold text-background-50 bg-primary-500 rounded-md">
              爆
            </span>
          )}
        </div>
        <div className="p-3 md:p-4">
          <h3 className="text-sm md:text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2 font-heading">
            {title}
          </h3>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span>{categoryName}</span>
            <span>{formatDate(publishedAt)}</span>
            <span>{formatViews(views)} 阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${id}`}
        className="group block bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300 transition-all overflow-hidden cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden relative">
          <img
            src={cardImageUrl}
            alt={title}
            title={`${title} - 吃瓜爆料网`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
          {isExplosive && (
            <span className="absolute top-3 right-3 px-2.5 py-1 text-xs font-bold text-background-50 bg-primary-500 rounded-md animate-pulse">
              爆
            </span>
          )}
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <h3 className="text-lg md:text-xl font-bold text-background-50 group-hover:text-primary-300 transition-colors line-clamp-2 mb-2 font-heading">
              {title}
            </h3>
            <p className="text-sm text-background-50/70 line-clamp-1">{excerpt}</p>
          </div>
        </div>
        <div className="p-3 md:p-4 flex items-center gap-3 text-xs text-foreground-500">
          <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
            {categoryName}
          </span>
          {isHot && (
            <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
              热
            </span>
          )}
          <span className="ml-auto">{formatDate(publishedAt)}</span>
          <span>{formatViews(views)} 阅读</span>
          <span>{comments} 评论</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${id}`}
      className="group block bg-background-50 rounded-lg border border-background-200/60 hover:border-primary-300 transition-all overflow-hidden cursor-pointer"
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img
          src={cardImageUrl}
          alt={title}
          title={`${title} - 吃瓜爆料网`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
        {isExplosive && (
          <span className="absolute top-2 right-2 px-2 py-0.5 text-xs font-bold text-background-50 bg-primary-500 rounded-md">
            爆
          </span>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-bold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2 font-heading">
          {title}
        </h3>
        <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3">
          {excerpt}
        </p>
        <div className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
          <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full text-xs font-medium">
            {categoryName}
          </span>
          {isHot && (
            <span className="px-2 py-0.5 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
              热
            </span>
          )}
          <span className="ml-auto">{formatDate(publishedAt)}</span>
          <span>{formatViews(views)} 阅读</span>
        </div>
      </div>
    </Link>
  );
}